import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQuery } from '@tanstack/react-query';
import {
  useAddUser, useMe, useOperatorsList, useRolesList, useSetUserRoles,
  selectableSites, useSetUserSites, useSitesList,
} from '../../api/queries';

const STEPS = ['Identity', 'Tenancy', 'Access', 'Confirm', 'Done'];

export type AddUserDialogProps = {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
};

/**
 * Three-step add-user wizard. The server creates an Active user with a
 * generated temporary password and forces them to change it on first
 * sign-in. Step 4 surfaces that temp password so the admin can hand it
 * to the new user out-of-band — there is no email server.
 */
export function AddUserDialog({ open, onClose, onAdded }: AddUserDialogProps) {
  const me = useMe();
  const add = useAddUser();
  const setRoles = useSetUserRoles();
  const setSites = useSetUserSites();
  const isArms = me.data?.is_arms ?? false;

  const operators = useOperatorsList();
  const operatorsQ = useQuery({
    queryKey: operators.queryKey,
    queryFn: operators.listFn,
    enabled: isArms && open,
  });
  const sitesList = useSitesList();
  const sitesQ    = useQuery({ queryKey: sitesList.queryKey, queryFn: sitesList.listFn,
                               enabled: open, staleTime: 60_000 });

  const [step, setStep] = useState(0);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  // ACES-only: choose between an ACES user (no operator) and an operator user.
  // Non-ACES callers always create an operator user in their own operator.
  const [makeAces, setMakeAces] = useState(false);
  // System users — Ed25519 service accounts (CI / agents). Mutually
  // exclusive with makeAces; both make a platform-tier user with no
  // operator. Server-side rails (channel-forcing, no promote permission)
  // mean a system user can never push directly to production.
  const [makeSystem, setMakeSystem] = useState(false);
  const [operatorIdInput, setOperatorIdInput] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Effective operator the new user will land in: ACES + ACES-toggle off →
  // user picks; ACES + toggle on → no operator (platform user); non-ACES
  // → caller's operator, never overridable. Computed early so the
  // roles query below can scope to the chosen operator and surface
  // that operator's clones (otherwise ACES sees nothing operator-tier).
  const isPlatformUser = isArms && (makeAces || makeSystem);
  const effectiveOperatorIdEarly: string | null = isArms
    ? (isPlatformUser ? null : operatorIdInput || null)
    : (me.data?.operator_id ?? null);

  const rolesList = useRolesList(effectiveOperatorIdEarly);
  const rolesQ    = useQuery({ queryKey: rolesList.queryKey, queryFn: rolesList.listFn,
                               enabled: open, staleTime: 60_000 });

  useEffect(() => {
    if (!open) {
      setStep(0);
      setUsername('');
      setEmail('');
      setDisplayName('');
      setMakeAces(false);
      setMakeSystem(false);
      setOperatorIdInput('');
      setSelectedRoleIds([]);
      setSelectedSiteIds([]);
      setTempPassword(null);
      setCopied(false);
      add.reset();
      setRoles.reset();
      setSites.reset();
    }
    // The mutation refs are unstable per render — including them would
    // loop the effect (reset() ticks state → re-render → fire again).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Alias for downstream readability — same value computed up top so
  // the roles query can use it.
  const effectiveOperatorId = effectiveOperatorIdEarly;

  // V9 tenancy: makeAces (or non-ACES caller adding ACES — unreachable)
  //   → Tier=Platform only. Templates (Tier=Operator, OperatorId=NULL)
  //   are not user-assignable; they exist only for cloning.
  const availableRoles = useMemo(() => {
    return (rolesQ.data ?? []).filter((r) => {
      if (!r.isActive) return false;
      if (isPlatformUser) return r.tier === 'Platform';
      if (effectiveOperatorId === null) return false;   // no op picked yet
      return r.tier === 'Operator' && r.operatorId === effectiveOperatorId;
    });
  }, [rolesQ.data, isPlatformUser, effectiveOperatorId]);

  const availableSites = useMemo(() => {
    if (isPlatformUser || effectiveOperatorId === null) return [];
    return selectableSites(sitesQ.data).filter((s) => s.operatorId === effectiveOperatorId);
  }, [sitesQ.data, isPlatformUser, effectiveOperatorId]);

  // Username: 3-64 chars, letters/digits/dot/underscore/dash, must start with letter/digit.
  // Mirrors the server-side regex so we fail fast in the UI.
  const usernameOk = /^[A-Za-z0-9][A-Za-z0-9._-]{2,63}$/.test(username.trim());
  // Email is optional. If provided, do a soft sanity check.
  const emailOk    = email.trim() === '' || /.+@.+\..+/.test(email.trim());
  const nameOk     = displayName.trim().length > 0;
  // Need at least one of username or email — username is the primary
  // login handle, email is the fallback (and used for transactional mail
  // once SMTP is wired). Username is required by the form so this is
  // always satisfied in practice.
  const handleOk      = usernameOk || (email.trim() !== '' && emailOk);
  // Operator selection rules:
  //   ACES caller, ACES or System toggle on → no operator needed
  //   ACES caller, both toggles off       → must pick an operator
  //   non-ACES caller                     → caller's own operator implicit
  const operatorIdOk  = isArms ? (isPlatformUser || operatorIdInput.length > 0) : true;

  const submit = async () => {
    const result = await add.mutateAsync({
      username:    username.trim(),
      email:       email.trim() || undefined,
      displayName: displayName.trim(),
      operatorId:  effectiveOperatorId,
      isArms:      isArms && makeAces,
      isSystem:    isArms && makeSystem,
    });
    // Apply role/site selections via the dedicated PUT endpoints.
    // Empty arrays are fine — that's the "operator-wide / no roles"
    // baseline and matches the new V8 semantics.
    if (selectedRoleIds.length > 0) {
      await setRoles.mutateAsync({ userId: result.user_id, roleIds: selectedRoleIds });
    }
    if (!isPlatformUser && selectedSiteIds.length > 0) {
      await setSites.mutateAsync({ userId: result.user_id, siteIds: selectedSiteIds });
    }
    setTempPassword(result.temporary_password);
    // Step 4 = Done; step 3 was Confirm. Going back to Confirm here
    // would re-show the Add button and a second click would attempt
    // a duplicate insert ("user already exists").
    setStep(4);
    onAdded();
  };

  const finish = () => {
    onClose();
  };

  const copyTemp = async () => {
    if (!tempPassword) return;
    await navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onClose={tempPassword ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="span">Add user</Typography>
        {!tempPassword && (
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((s) => (
            <Step key={s}><StepLabel>{s}</StepLabel></Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Username + display name are required. Email is optional —
              the user can sign in with either; email is needed only if
              you want to send them notifications later.
            </Typography>
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              fullWidth
              autoFocus
              error={username !== '' && !usernameOk}
              helperText={username !== '' && !usernameOk
                ? '3–64 chars: letters, digits, dot, underscore, dash. Must start with letter or digit.'
                : 'Globally unique. Case-insensitive at sign-in.'}
            />
            {!makeSystem && (
              <TextField
                label="Email (optional)"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                error={email !== '' && !emailOk}
                helperText={email !== '' && !emailOk
                  ? 'Must look like an email address.'
                  : 'Used for sign-in fallback and notifications.'}
              />
            )}
            <TextField
              label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              fullWidth
            />
          </Stack>
        )}

        {step === 1 && (
          <Stack spacing={2}>
            {isArms ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  ACES users see every operator's data. Pick an operator
                  here only if you're adding a non-ACES user.
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={makeAces} onChange={(e) => {
                    setMakeAces(e.target.checked);
                    if (e.target.checked) {
                      setMakeSystem(false);
                      setOperatorIdInput('');
                      setSelectedSiteIds([]);
                      setSelectedRoleIds([]);
                    }
                  }} />}
                  label="ARMS user (platform-wide; no operator)"
                />
                <FormControlLabel
                  control={<Checkbox checked={makeSystem} onChange={(e) => {
                    setMakeSystem(e.target.checked);
                    if (e.target.checked) {
                      setMakeAces(false);
                      setEmail('');
                      setOperatorIdInput('');
                      setSelectedSiteIds([]);
                      setSelectedRoleIds([]);
                    }
                  }} />}
                  label="System user (Ed25519 service account — for CI / agents)"
                />
                {makeSystem && (
                  <Alert severity="info">
                    System users authenticate via the Ed25519 challenge-response
                    flow (no UI sign-in). Server-side rails force any artefact
                    they upload onto the <strong>beta</strong> channel and
                    block channel-head promotion — an ACES user must explicitly
                    promote a system upload before it deploys to a fleet.
                    After creation, register the public key under
                    <code> /admin/users/&lt;id&gt;/keys</code>.
                  </Alert>
                )}
                {!isPlatformUser && (
                  <TextField
                    select
                    label="Operator"
                    value={operatorIdInput}
                    onChange={(e) => {
                      setOperatorIdInput(e.target.value);
                      // Site list is per-operator — reset on operator change
                      // so we don't keep stale selections from a previous pick.
                      setSelectedSiteIds([]);
                      setSelectedRoleIds([]);
                    }}
                    required fullWidth
                    helperText={operatorsQ.isLoading ? 'Loading operators…' : 'Required.'}
                    disabled={operatorsQ.isLoading}
                  >
                    {(operatorsQ.data ?? []).map((op) => (
                      <MenuItem key={op.id} value={op.id}>
                        {op.displayName} ({op.code})
                      </MenuItem>
                    ))}
                    {(!operatorsQ.isLoading && (operatorsQ.data?.length ?? 0) === 0) && (
                      <MenuItem disabled value="">
                        No operators yet — create one first on the Operators page.
                      </MenuItem>
                    )}
                  </TextField>
                )}
              </>
            ) : (
              <Alert severity="info">
                The new user will be added to your operator. Operator
                selection is locked — only ACES users can place users
                in other operators or create platform-tier ACES users.
              </Alert>
            )}
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Pick the roles the user holds (permissions are the union of
              every assigned role) and, for an operator user, optionally
              narrow them to specific sites. Leaving the site picker
              empty grants them every site of their operator — including
              new sites added later.
            </Typography>

            <FormControl size="small" fullWidth>
              <InputLabel id="add-user-roles-label">Roles</InputLabel>
              <Select
                labelId="add-user-roles-label"
                multiple
                value={selectedRoleIds}
                onChange={(e) => setSelectedRoleIds(typeof e.target.value === 'string'
                  ? e.target.value.split(',') : e.target.value)}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((rid) => {
                      const r = availableRoles.find((x) => x.id === rid);
                      return <Chip key={rid} size="small" label={r?.displayName ?? rid.slice(0, 8)} />;
                    })}
                  </Box>
                )}
              >
                {availableRoles.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    <Checkbox checked={selectedRoleIds.includes(r.id)} />
                    <ListItemText primary={r.displayName} secondary={`${r.code} · ${r.tier}`} />
                  </MenuItem>
                ))}
                {availableRoles.length === 0 && (
                  <MenuItem disabled>
                    {isPlatformUser ? 'No platform-tier roles available.' : 'No roles available for this operator.'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {!isPlatformUser && (
              <FormControl size="small" fullWidth>
                <InputLabel id="add-user-sites-label">Sites (empty = all operator sites)</InputLabel>
                <Select
                  labelId="add-user-sites-label"
                  multiple
                  value={selectedSiteIds}
                  onChange={(e) => setSelectedSiteIds(typeof e.target.value === 'string'
                    ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput label="Sites (empty = all operator sites)" />}
                  renderValue={(selected) => (selected as string[]).length === 0
                    ? <em style={{ color: 'rgba(0,0,0,0.5)' }}>All operator sites</em>
                    : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((sid) => {
                          const s = availableSites.find((x) => x.id === sid);
                          return <Chip key={sid} size="small" label={s?.displayName ?? sid.slice(0, 8)} />;
                        })}
                      </Box>
                    )}
                >
                  {availableSites.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={selectedSiteIds.includes(s.id)} />
                      <ListItemText primary={s.displayName} secondary={s.code} />
                    </MenuItem>
                  ))}
                  {availableSites.length === 0 && (
                    <MenuItem disabled>No sites configured for this operator yet.</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </Stack>
        )}

        {step === 3 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Review and submit. A temporary password will be generated; the
              user must change it on first sign-in.
            </Typography>
            <Detail label="Username" value={username} />
            <Detail label="Email" value={email || '—'} />
            <Detail label="Display name" value={displayName} />
            <Detail label="Tenancy" value={
              makeSystem
                ? 'System (Ed25519 service account, platform-wide)'
                : makeAces
                ? 'ACES (platform-wide)'
                : (operatorsQ.data?.find((o) => o.id === effectiveOperatorId)?.displayName
                   ?? me.data?.operator_id ?? effectiveOperatorId ?? '—')
            } />
            <Detail label="Account kind" value={
              makeSystem ? 'System' : makeAces ? 'ACES (human)' : 'Operator (human)'
            } />
            <Detail label="Roles" value={
              selectedRoleIds.length === 0 ? '— (none)' :
              selectedRoleIds
                .map((rid) => availableRoles.find((r) => r.id === rid)?.displayName ?? rid.slice(0, 8))
                .join(', ')
            } />
            {!isPlatformUser && (
              <Detail label="Sites" value={
                selectedSiteIds.length === 0 ? 'All operator sites' :
                `${selectedSiteIds.length} selected`
              } />
            )}
            {add.error && (
              <ErrorAlert>
                {(add.error as { message?: string }).message ?? 'Add user failed.'}
              </ErrorAlert>
            )}
          </Stack>
        )}

        {step === 4 && tempPassword && (
          <Stack spacing={2}>
            <Alert severity="success">
              User <strong>{username}</strong> has been added.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Share this temporary password with the user. They will be
              required to set a new one on first sign-in. This password
              will not be shown again.
            </Typography>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              p: 2, borderRadius: 1, bgcolor: 'background.default',
              border: 1, borderColor: 'divider',
            }}>
              <Typography sx={{ flex: 1, fontFamily: 'monospace', fontSize: 16, wordBreak: 'break-all' }}>
                {tempPassword}
              </Typography>
              <Tooltip title={copied ? 'Copied' : 'Copy'}>
                <IconButton onClick={copyTemp} size="small">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step < 4 && (
          <>
            <Button onClick={onClose} disabled={add.isPending || setRoles.isPending || setSites.isPending}>
              Cancel
            </Button>
            {step > 0 && (
              <Button onClick={() => setStep(step - 1)} disabled={add.isPending}>Back</Button>
            )}
            {step < 3 ? (
              <Button
                variant="contained"
                disabled={
                  (step === 0 && (!handleOk || !usernameOk || !emailOk || !nameOk)) ||
                  (step === 1 && !operatorIdOk)
                  // step 2 (Access) — roles and sites are both optional
                }
                onClick={() => setStep(step + 1)}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={add.isPending || setRoles.isPending || setSites.isPending}
                onClick={submit}
              >
                {add.isPending ? 'Adding…' : 'Add user'}
              </Button>
            )}
          </>
        )}
        {step === 4 && (
          <Button variant="contained" onClick={finish}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{value || '—'}</Typography>
    </Stack>
  );
}
